import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { virtualCompetitionAPI } from '../api/endpoints';
import {
  FaUsers, FaTrophy, FaCoins, FaCheck, FaClock,
  FaHourglassHalf, FaFire, FaMedal, FaCrown,
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './VirtualCompetitionPlay.css';

const VirtualCompetitionPlay = () => {
  const { slug } = useParams();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [submittingRoundId, setSubmittingRoundId] = useState(null);

  const fetchGame = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const res = await virtualCompetitionAPI.getBySlug(slug);
      setGame(res.data);
      if (res.data?.status === 'live' || res.data?.status === 'completed') {
        try {
          const lb = await virtualCompetitionAPI.leaderboard(res.data.id);
          setLeaderboard(lb.data || []);
        } catch {
          setLeaderboard([]);
        }
      }
    } catch (err) {
      if (showLoader) toast.error('Competition not found');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchGame(true); }, [fetchGame]);

  // Poll while live
  useEffect(() => {
    if (!game || game.status !== 'live') return;
    const t = setInterval(() => fetchGame(false), 10000);
    return () => clearInterval(t);
  }, [game, fetchGame]);

  const handleJoin = async () => {
    if (!isAuthenticated) return toast.error('Please log in');
    try {
      const res = await virtualCompetitionAPI.join(slug);
      if (res.data?.success) {
        toast.success('Joined! Wait for the creator to start.');
        fetchGame(false);
      } else {
        toast.error(res.data?.message || 'Failed to join');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to join');
    }
  };

  const handlePick = async (roundId, outcome) => {
    if (!game) return;
    setSubmittingRoundId(roundId);
    try {
      const res = await virtualCompetitionAPI.submitPick(game.id, roundId, outcome);
      if (res.data?.success) {
        toast.success('Pick locked in!');
        fetchGame(false);
      } else {
        toast.error(res.data?.message || 'Failed to submit');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit');
    } finally {
      setSubmittingRoundId(null);
    }
  };

  if (loading) return <div className="vcp-loading"><LoadingSpinner size="large" /></div>;
  if (!game) return <div className="vcp-empty"><p>Competition not found.</p></div>;

  const isCreator = user && String(user.id) === String(game.creator_id);
  const isPlayer = !!game.my_participation;
  const canJoin = game.status === 'open' && !isCreator && !isPlayer;
  const roundClosesIn = game.round_closes_at
    ? Math.max(0, new Date(game.round_closes_at + 'Z').getTime() - Date.now())
    : 0;
  const closesMin = Math.floor(roundClosesIn / 60000);
  const closesSec = Math.floor((roundClosesIn % 60000) / 1000);

  return (
    <div className="vcp-page">
      {/* Header */}
      <div className="vcp-header">
        <div className="vcp-creator">
          {game.creator_avatar && <img src={game.creator_avatar} alt="" />}
          <span>Hosted by <strong>{game.creator_name}</strong></span>
        </div>
        <span className={`vcp-status ${game.status}`}>{game.status.toUpperCase()}</span>
      </div>

      <h1 className="vcp-title">{game.name}</h1>
      {game.description && <p className="vcp-desc">{game.description}</p>}

      {/* Stats strip */}
      <div className="vcp-stats">
        <div><FaCoins /> {Number(game.prize_pool).toFixed(2)} prize pool</div>
        <div><FaUsers /> {game.player_count} players</div>
        <div><FaTrophy /> {game.total_winners} winners</div>
        <div>Entry: {Number(game.entry_fee) > 0 ? `${Number(game.entry_fee).toFixed(2)}` : 'Free'}</div>
      </div>

      {/* Progress */}
      <div className="vcp-progress-wrap">
        <div className="vcp-progress-bar">
          <div
            className="vcp-progress-fill"
            style={{ width: `${Math.min(100, (game.games_played / game.total_games) * 100)}%` }}
          />
        </div>
        <span>{game.games_played} / {game.total_games} games</span>
      </div>

      {/* Join button (open state) */}
      {canJoin && (
        <button className="btn btn-primary vcp-join-btn" onClick={handleJoin}>
          Join Competition {Number(game.entry_fee) > 0 && `— ${Number(game.entry_fee).toFixed(2)} $XPUNT`}
        </button>
      )}

      {isCreator && game.status === 'open' && (
        <div className="vcp-note info">
          You are the host. Share the link, then click Start in your dashboard when ready.
        </div>
      )}

      {isPlayer && game.status === 'open' && (
        <div className="vcp-note">You're in! Waiting for the host to start.</div>
      )}

      {/* Live round view */}
      {game.status === 'live' && (
        <>
          <div className="vcp-round-timer">
            <FaClock /> Round {game.current_round} closes in {closesMin}:{String(closesSec).padStart(2, '0')}
          </div>

          <div className="vcp-rounds">
            {(game.current_rounds || []).map((round) => {
              const settled = round.status === 'settled';
              const myPick = round.my_pick;
              const canPick = !settled && !myPick && isPlayer;

              return (
                <div key={round.id} className={`vcp-round ${settled ? 'settled' : 'open'}`}>
                  <div className="vcp-round-teams">
                    <div className="vcp-team">
                      {round.home_club_logo && <img src={round.home_club_logo} alt="" />}
                      <span>{round.home_team}</span>
                      {settled && <strong>{round.home_score}</strong>}
                    </div>
                    <span className="vs">vs</span>
                    <div className="vcp-team away">
                      {round.away_club_logo && <img src={round.away_club_logo} alt="" />}
                      <span>{round.away_team}</span>
                      {settled && <strong>{round.away_score}</strong>}
                    </div>
                  </div>

                  {!settled && (
                    <div className="vcp-pick-buttons">
                      {['Home Win', 'Draw', 'Away Win'].map((o) => (
                        <button
                          key={o}
                          type="button"
                          disabled={!canPick || submittingRoundId === round.id}
                          className={myPick?.outcome === o ? 'active' : ''}
                          onClick={() => handlePick(round.id, o)}
                        >
                          {submittingRoundId === round.id ? <LoadingSpinner size="small" /> : o}
                        </button>
                      ))}
                    </div>
                  )}

                  {myPick && (
                    <div className={`vcp-my-pick ${settled ? (myPick.is_correct ? 'correct' : 'wrong') : 'locked'}`}>
                      {settled ? (
                        <>
                          {myPick.is_correct ? <FaCheck /> : '✗'} Your pick: <strong>{myPick.outcome}</strong>
                          {settled && ` · +${myPick.points_earned} pts`}
                        </>
                      ) : (
                        <>🔒 Locked in: <strong>{myPick.outcome}</strong></>
                      )}
                    </div>
                  )}

                  {settled && (
                    <div className="vcp-round-result">
                      Result: <strong>{round.result}</strong>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Completed state */}
      {game.status === 'completed' && (
        <div className="vcp-completed">
          <FaCheck /> Competition completed — final leaderboard below
        </div>
      )}

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div className="vcp-leaderboard">
          <h3>🏆 Leaderboard</h3>
          <div className="vcp-lb-head">
            <span>Rank</span>
            <span>Player</span>
            <span>Points</span>
            <span>Correct</span>
          </div>
          {leaderboard.map((row) => (
            <div
              key={row.user_id}
              className={`vcp-lb-row rank-${row.rank} ${row.is_me ? 'is-me' : ''}`}
            >
              <span className="lb-rank">
                {row.rank === 1 ? <FaCrown /> : row.rank === 2 ? <FaMedal /> : row.rank === 3 ? <FaMedal /> : `#${row.rank}`}
              </span>
              <span className="lb-name">
                {row.avatar && <img src={row.avatar} alt="" />}
                {row.name}
              </span>
              <span className="lb-points">{row.total_points}</span>
              <span className="lb-correct">{row.correct_predictions} / {row.total_predictions}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VirtualCompetitionPlay;